import { useEffect, useMemo, useRef, useState } from "react";

/**
 * Экран выдачи — телевизор в фудтраке.
 *
 * Две колонки и крупные номера. Читается с нескольких метров, поэтому здесь нет
 * ничего, кроме номеров: ни состава, ни сумм, ни имён. Всё, что появится на
 * этом экране, увидит каждый прохожий.
 */

interface BoardOrder {
  number: string;
  type: "delivery" | "pickup";
}

interface BoardSnapshot {
  cooking: BoardOrder[];
  ready: BoardOrder[];
  at: string;
}

export function BoardScreen({ code }: { code: string }) {
  const [snapshot, setSnapshot] = useState<BoardSnapshot | null>(null);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(() => new Date());

  // Номера, появившиеся в «Готовы» только что: их видно по анимации, иначе
  // на неподвижном экране изменение легко пропустить.
  const [fresh, setFresh] = useState<Set<string>>(new Set());
  const previousReady = useRef<Set<string>>(new Set());

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const query = `code=${encodeURIComponent(code)}`;

    fetch(`/api/public/board?${query}`)
      .then(async response => {
        if (!response.ok) throw new Error("Экран не открылся: проверьте ссылку");
        return response.json() as Promise<BoardSnapshot>;
      })
      .then(data => {
        if (!cancelled) setSnapshot(data);
      })
      .catch(cause => {
        if (!cancelled) setError(cause instanceof Error ? cause.message : "Экран не открылся");
      });

    const stream = new EventSource(`/api/public/board/events?${query}`);

    const apply = (event: MessageEvent<string>) => {
      const parsed = JSON.parse(event.data) as { payload: BoardSnapshot | null };
      if (parsed.payload) setSnapshot(parsed.payload);
      setConnected(true);
      setError(null);
    };

    stream.addEventListener("hello", apply as EventListener);
    stream.addEventListener("state", apply as EventListener);
    stream.onopen = () => setConnected(true);
    stream.onerror = () => setConnected(false);

    return () => {
      cancelled = true;
      stream.close();
    };
  }, [code]);

  const ready = useMemo(() => snapshot?.ready ?? [], [snapshot]);

  useEffect(() => {
    const current = new Set(ready.map(order => order.number));
    const added = [...current].filter(number => !previousReady.current.has(number));
    previousReady.current = current;

    if (added.length === 0) return;

    setFresh(new Set(added));
    const timer = setTimeout(() => setFresh(new Set()), 12_000);
    return () => clearTimeout(timer);
  }, [ready]);

  if (error) {
    return (
      <div className="board board-error pf-dark">
        <p>{error}</p>
      </div>
    );
  }

  const cooking = snapshot?.cooking ?? [];

  return (
    <div className="board pf-dark">
      <header className="board-head">
        <span className="board-clock">
          {now.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" })}
        </span>
        <span className={`board-link ${connected ? "" : "offline"}`}>
          {connected ? "" : "нет связи"}
        </span>
      </header>

      <div className="board-columns">
        <section className="board-column">
          <h2>Готовятся</h2>
          {cooking.length === 0 ? (
            <p className="board-empty">—</p>
          ) : (
            <ul>
              {cooking.map(order => (
                <li key={order.number}>{order.number}</li>
              ))}
            </ul>
          )}
        </section>

        <section className="board-column board-column-ready">
          <h2>Готовы</h2>
          {ready.length === 0 ? (
            <p className="board-empty">—</p>
          ) : (
            <ul>
              {ready.map(order => (
                <li key={order.number} className={fresh.has(order.number) ? "fresh" : ""}>
                  {order.number}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
